import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { securityService } from "services/SecurityService";
import { getException } from "lib/helpers/errorHelper";
import { Group } from "types";

const name = "groups.action";

interface GroupState {
  error: boolean;
  groups: Group[];
  loading: Boolean;
  severity: string;
  showToast: boolean;
  response: any | null;
  currentGroup: Group | null;
}

const initialState: GroupState = {
  groups: [],
  error: false,
  loading: false,
  response: null,
  showToast: false,
  currentGroup: null,
  severity: "success",
};

export const createGroup = createAsyncThunk(`${name}/createGroup`, async (group: Group, { rejectWithValue }) => {
  try {
    const response = await securityService.saveGroup(group);
    return response;
  } catch ({ data }) {
    return rejectWithValue(getException(data));
  }
});
export const getGroups = createAsyncThunk(`${name}/getGroups`, async () => await securityService.getGroups());

export const editGroup = createAsyncThunk(`${name}/editGroup`, async (group: Group, { rejectWithValue }) => {
  try {
    const response = await securityService.updateGroup(group);
    return response;
  } catch ({ data }) {
    return rejectWithValue(getException(data));
  }
});

export const getGroupById = createAsyncThunk(`${name}/getGroupById`, async (groupId: string, { rejectWithValue }) => {
  try {
    const response = await securityService.getGroup(groupId);
    return response;
  } catch ({ data }) {
    return rejectWithValue(getException(data));
  }
});

export const deleteGroup = createAsyncThunk(`${name}/deleteGroup`, async (groupId: number, { rejectWithValue }) => {
  try {
    const response = await securityService.deleteGroup(groupId);
    return response;
  } catch ({ data }) {
    return rejectWithValue(getException(data));
  }
});

const groupsSlice = createSlice({
  name,
  initialState,
  reducers: {
    hideToast: (state) => {
      state.showToast = false;
    },
  },
  extraReducers: (builder) => {
    /* GET GROUPS */
    builder.addCase(getGroups.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getGroups.fulfilled, (state, { payload }) => {
      state.error = false;
      state.loading = false;
      state.groups = payload;
    });

    builder.addCase(getGroups.rejected, (state) => {
      state.error = true;
      state.loading = false;
    });
    /* CREATE GROUPS */
    builder.addCase(createGroup.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createGroup.fulfilled, (state, { payload }) => {
      state.error = false;
      state.loading = false;
      state.showToast = true;
      state.severity = "success";
      state.response = payload;
    });
    builder.addCase(createGroup.rejected, (state, { payload }) => {
      state.error = true;
      state.loading = false;
      state.showToast = true;
      state.severity = "error";
      state.response = payload;
    });
    /* GET GROUP BY ID */
    builder.addCase(getGroupById.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getGroupById.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.currentGroup = payload;
    });
    builder.addCase(getGroupById.rejected, (state, { payload }) => {
      state.error = true;
      state.loading = false;
      state.showToast = true;
      state.severity = "error";
      state.response = payload;
    });
    /* UPDATE GROUP */
    builder.addCase(editGroup.pending, (state) => {
      state.response = null;
      state.loading = true;
    });
    builder.addCase(editGroup.fulfilled, (state, { payload }) => {
      state.error = false;
      state.loading = false;
      state.showToast = true;
      state.severity = "success";
      state.response = payload;
    });
    builder.addCase(editGroup.rejected, (state, { payload }) => {
      state.error = true;
      state.loading = false;
      state.showToast = true;
      state.severity = "error";
      state.response = payload;
    });
    /* DELETE GROUP */
    builder.addCase(deleteGroup.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteGroup.fulfilled, (state, { payload, meta: { arg } }) => {
      state.error = false;
      state.loading = false;
      state.showToast = true;
      state.severity = "success";
      state.response = payload;
      state.groups = state.groups.filter((x) => x.groupID !== arg);
    });
    builder.addCase(deleteGroup.rejected, (state, { payload }) => {
      state.error = true;
      state.loading = false;
      state.showToast = true;
      state.severity = "error";
      state.response = payload;
    });
  },
});

export const { hideToast } = groupsSlice.actions;

export default groupsSlice.reducer;
